import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, getDoc, setDoc, addDoc, updateDoc, deleteDoc, doc, query, where, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { googleContactsCsv } from '../utils/googleContactsCsv';
import { paidQty, owedQty } from '../utils/bulkOrderPaid';
import { db } from '../firebase';
import Layout from '../components/Layout';
import {
  Box, Typography, Button, IconButton, TextField, Switch, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, MenuItem,
  TableCell, TableContainer, TableHead, TableRow, Paper, Alert, Chip, Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// How group-order money reaches the club. Recorded per row so each total can be
// checked against the Venmo and Zelle histories and the cash in hand.
const PAID_METHODS = ['Venmo', 'Zelle', 'Cash'];

/** Last ten digits, the same key the server files group-order rows under. */
const phoneKey = (phone) => String(phone || '').replace(/\D/g, '').slice(-10);

const emptyAddRow = { firstName: '', lastName: '', phone: '', email: '', quantity: '1', payment: 'unpaid' };

const emptyCost = { unitCost: '', quantity: '', subtotal: '', shipping: '', tax: '', taxEstimated: true, saveEstimate: true };

const money = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;
const toCents = (v) => Math.round((Number(String(v).replace(/[$,]/g, '')) || 0) * 100);

/**
 * What a group order costs the club. Placed: the receipt as entered. Not placed
 * yet: the committed quantity at the order's cost per item, plus shipping and
 * tax at the rates from the last order (settings/finance.groupOrderEstimate).
 * Null when there is nothing to go on.
 */
function orderCost(cost, estimate, committed) {
  if (cost?.placedTotalCents) {
    return { placed: true, quantity: Number(cost.placedQuantity) || 0, items: cost.placedSubtotalCents || 0,
      shipping: cost.placedShippingCents || 0, tax: cost.placedTaxCents || 0,
      taxEstimated: cost.placedTaxEstimated === true, total: cost.placedTotalCents };
  }
  if (!cost?.unitCostCents || !committed) return null;
  const items = committed * cost.unitCostCents;
  const shipping = Number(estimate?.shippingCents) || 0;
  const tax = Math.round((Number(estimate?.taxRate) || 0) * (items + (estimate?.taxOnShipping ? shipping : 0)));
  return { placed: false, quantity: committed, items, shipping, tax, taxEstimated: true, total: items + shipping + tax };
}

const emptyForm = { name: '', description: '', price: '', sizesText: 'S, M, L, XL', imageUrl: '', active: true, sortOrder: '' };

export default function AdminStoreItems() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // doc id or null = new
  const [form, setForm] = useState(emptyForm);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  // Group orders and who is on them. The store page shows only a total, so this
  // is the only place the list can be read — and the only place a quantity can
  // be corrected for somebody who phoned rather than used the app.
  const [bulkOrders, setBulkOrders] = useState([]);
  const [signups, setSignups] = useState({});
  // Accounts by phone number, for rows that came in without one attached — the
  // sheet import, pledges added by hand, and people who did not sign in. Shown
  // only: a phone match is not proof of identity, so it never sets userId.
  const [usersByPhone, setUsersByPhone] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  // Adding somebody by hand — a pledge made in person, by text or in the group
  // chat rather than through the store page.
  const [addFor, setAddFor] = useState(null); // the order being added to, or null
  const [addRow, setAddRow] = useState(emptyAddRow);
  const [editRow, setEditRow] = useState(null); // the signup being edited, or null
  // What each order costs the club, admin-only (bulkOrders is public, and the
  // store page must never show the club's buying price), and the shipping and
  // tax rates from the last order, which estimate the next one.
  const [costs, setCosts] = useState({});
  const [costEstimate, setCostEstimate] = useState(null);
  const [costFor, setCostFor] = useState(null); // the order whose cost is being entered, or null
  const [costForm, setCostForm] = useState(emptyCost);

  const loadBulk = () => {
    getDocs(collection(db, 'users'))
      .then(snap => {
        const map = {};
        snap.docs.forEach(d => {
          const u = d.data();
          const key = phoneKey(u.phone);
          // Merged-away logins resolve to another account, which is listed in
          // its own right.
          if (key.length !== 10 || u.mergedIntoUid || d.id.startsWith('pending_')) return;
          (map[key] = map[key] || []).push({ id: d.id, ...u });
        });
        setUsersByPhone(map);
      })
      .catch(() => setUsersByPhone({}));
    getDocs(collection(db, 'bulkOrderCosts'))
      .then(snap => setCosts(Object.fromEntries(snap.docs.map(d => [d.id, d.data()]))))
      .catch(() => setCosts({}));
    getDoc(doc(db, 'settings', 'finance'))
      .then(d => setCostEstimate(d.data()?.groupOrderEstimate || null))
      .catch(() => setCostEstimate(null));
    getDocs(collection(db, 'bulkOrders'))
      .then(async snap => {
        const orders = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setBulkOrders(orders);
        const rows = {};
        for (const o of orders) {
          // Equality on orderId only. Asking Firestore to order by firstName as
          // well needs a composite index that does not exist, so the query threw
          // and the list read as empty — a list of 23 sorts here for nothing.
          const r = await getDocs(query(collection(db, 'bulkOrderSignups'),
            where('orderId', '==', o.id)));
          rows[o.id] = r.docs.map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => `${a.firstName} ${a.lastName}`.trim()
              .localeCompare(`${b.firstName} ${b.lastName}`.trim()));
        }
        setSignups(rows);
      })
      // Said out loud. Swallowed, a failed read here is indistinguishable from
      // an empty order — which is exactly how the missing index hid.
      .catch(e => {
        setBulkOrders([]);
        setError(`Could not load the group orders: ${e.message}`);
      });
  };
  useEffect(loadBulk, []);

  const setStatus = async (order, status) => {
    setBulkBusy(true); setError('');
    try {
      await httpsCallable(getFunctions(), 'setBulkOrderStatus')({ orderId: order.id, status });
      loadBulk();
    } catch (e) { setError(e.message || 'Could not change the status.'); }
    setBulkBusy(false);
  };

  const openCost = (order) => {
    const c = costs[order.id] || {};
    const dollars = (v) => (v ? (v / 100).toFixed(2) : '');
    setCostForm({
      unitCost: dollars(c.unitCostCents), quantity: c.placedQuantity ? String(c.placedQuantity) : '',
      subtotal: dollars(c.placedSubtotalCents), shipping: dollars(c.placedShippingCents), tax: dollars(c.placedTaxCents),
      taxEstimated: c.placedTaxEstimated !== false, saveEstimate: true,
    });
    setCostFor(order);
  };

  const saveCost = async () => {
    setBulkBusy(true); setError('');
    try {
      const f = costForm;
      const subtotal = toCents(f.subtotal);
      const shipping = toCents(f.shipping);
      const tax = toCents(f.tax);
      const placed = subtotal > 0;
      await setDoc(doc(db, 'bulkOrderCosts', costFor.id), {
        orderId: costFor.id,
        unitCostCents: toCents(f.unitCost) || null,
        placedQuantity: placed ? (Number(f.quantity) || 0) : null,
        placedSubtotalCents: placed ? subtotal : null,
        placedShippingCents: placed ? shipping : null,
        placedTaxCents: placed ? tax : null,
        placedTaxEstimated: placed ? f.taxEstimated === true : null,
        placedTotalCents: placed ? subtotal + shipping + tax : null,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      // The next order is estimated from this one: the same shipping, and tax
      // at the rate this receipt charged on items and shipping together.
      if (placed && f.saveEstimate) {
        await setDoc(doc(db, 'settings', 'finance'), {
          groupOrderEstimate: {
            shippingCents: shipping,
            taxRate: Math.round((tax / (subtotal + shipping)) * 10000) / 10000,
            taxOnShipping: true,
            basedOn: costFor.name || costFor.id,
            updatedAt: serverTimestamp(),
          },
        }, { merge: true });
      }
      setCostFor(null);
      loadBulk();
    } catch (e) { setError(e.message || 'Could not save the cost.'); }
    setBulkBusy(false);
  };

  const setDelivered = async (row, delivered) => {
    setBulkBusy(true); setError('');
    try {
      await httpsCallable(getFunctions(), 'adminSetBulkOrderSignup')({ signupId: row.id, delivered });
      loadBulk();
    } catch (e) { setError(e.message || 'Could not change that.'); }
    setBulkBusy(false);
  };

  const setPaid = async (row, paid, paidMethod) => {
    setBulkBusy(true); setError('');
    try {
      await httpsCallable(getFunctions(), 'adminSetBulkOrderSignup')({
        signupId: row.id, paid, ...(paid && paidMethod ? { paidMethod } : {}),
      });
      loadBulk();
    } catch (e) { setError(e.message || 'Could not change that payment status.'); }
    setBulkBusy(false);
  };

  const setQuantity = async (row, quantity) => {
    setBulkBusy(true); setError('');
    try {
      await httpsCallable(getFunctions(), 'adminSetBulkOrderSignup')({ signupId: row.id, quantity });
      loadBulk();
    } catch (e) { setError(e.message || 'Could not change that quantity.'); }
    setBulkBusy(false);
  };

  const saveAddRow = async () => {
    const quantity = Math.floor(Number(addRow.quantity));
    if (!addRow.firstName.trim()) { setError('A first name is needed.'); return; }
    if (!Number.isFinite(quantity) || quantity < 0 || quantity > 20) {
      setError('Quantity must be between 0 and 20.'); return;
    }
    setBulkBusy(true); setError('');
    try {
      const fn = httpsCallable(getFunctions(), 'adminSetBulkOrderSignup');
      const { data } = await fn({
        orderId: addFor.id, quantity,
        firstName: addRow.firstName, lastName: addRow.lastName,
        phone: addRow.phone, email: addRow.email,
      });
      // Paid is its own call, the same one the dropdown makes, so a row added
      // with the money already in hand is recorded exactly like one ticked later.
      if (addRow.payment !== 'unpaid' && quantity > 0) {
        await fn({ signupId: data.signupId, paid: true, paidMethod: addRow.payment });
      }
      setAddFor(null);
      setMsg(`${addRow.firstName.trim()} added to ${addFor.name}.`);
      loadBulk();
    } catch (e) { setError(e.message || 'Could not add that person.'); }
    setBulkBusy(false);
  };

  const openEditRow = (row) => {
    setEditRow(row);
    setAddRow({
      firstName: row.firstName || '', lastName: row.lastName || '',
      phone: row.phone || '', email: row.email || '',
      quantity: String(Number(row.quantity) || 0),
      paidFor: String(paidQty(row)),
      payment: row.paidMethod || (paidQty(row) > 0 ? PAID_METHODS[0] : 'unpaid'),
    });
  };

  // Name, phone, email, quantity and how many are paid for, in one save. A
  // member's name and phone are refreshed from their account the next time
  // they change their own order, so a member's details are best fixed there.
  const saveEditRow = async () => {
    const quantity = Math.floor(Number(addRow.quantity));
    const paidFor = Math.floor(Number(addRow.paidFor));
    if (!addRow.firstName.trim()) { setError('A first name is needed.'); return; }
    if (!Number.isFinite(quantity) || quantity < 0 || quantity > 20) {
      setError('Quantity must be between 0 and 20.'); return;
    }
    if (!Number.isFinite(paidFor) || paidFor < 0 || paidFor > 20) {
      setError('Paid for must be between 0 and 20.'); return;
    }
    setBulkBusy(true); setError('');
    try {
      await httpsCallable(getFunctions(), 'adminSetBulkOrderSignup')({
        signupId: editRow.id, quantity,
        firstName: addRow.firstName, lastName: addRow.lastName,
        phone: addRow.phone, email: addRow.email,
        quantityPaid: paidFor,
        ...(paidFor > 0 && addRow.payment !== 'unpaid' ? { paidMethod: addRow.payment } : {}),
      });
      setMsg(`${addRow.firstName.trim()} updated.`);
      setEditRow(null);
      loadBulk();
    } catch (e) { setError(e.message || 'Could not save those changes.'); }
    setBulkBusy(false);
  };

  const removeRow = async (row) => {
    if (!window.confirm(`Take ${row.firstName} ${row.lastName} off this order?`)) return;
    setBulkBusy(true); setError('');
    try {
      await httpsCallable(getFunctions(), 'adminSetBulkOrderSignup')({ signupId: row.id, remove: true });
      loadBulk();
    } catch (e) { setError(e.message || 'Could not remove that row.'); }
    setBulkBusy(false);
  };

  // The list as contacts, for the message that goes out when the order lands.
  const exportSignups = (order) => {
    const rows = (signups[order.id] || []).filter(r => (Number(r.quantity) || 0) > 0).map(r => ({
      firstName: r.firstName, lastName: r.lastName, email: r.email, phone: r.phone,
      notes: `${order.name} — ${r.quantity}`, organization: '',
    }));
    const blob = new Blob(['\ufeff' + googleContactsCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${String(order.name || 'order').replace(/\s+/g, '_')}_contacts.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const load = () => {
    getDocs(collection(db, 'apparel'))
      .then(snap => setItems(
        snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.sortOrder ?? 99) - (b.sortOrder ?? 99) || (a.name || '').localeCompare(b.name || ''))
      ))
      .catch(err => setError('Load failed: ' + err.message));
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (item) => {
    setEditing(item.id);
    setForm({
      name: item.name || '',
      description: item.description || '',
      price: String(item.price ?? ''),
      sizesText: Array.isArray(item.sizes) ? item.sizes.join(', ') : '',
      imageUrl: item.imageUrl || '',
      active: item.active !== false,
      sortOrder: String(item.sortOrder ?? ''),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setError('');
    const price = parseFloat(form.price);
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!Number.isFinite(price) || price <= 0) { setError('Price must be a positive number.'); return; }
    const data = {
      name: form.name.trim(),
      description: form.description.trim(),
      price,
      sizes: form.sizesText.split(',').map(s => s.trim()).filter(Boolean),
      imageUrl: form.imageUrl.trim(),
      active: form.active,
      sortOrder: form.sortOrder === '' ? 99 : parseInt(form.sortOrder, 10),
    };
    try {
      if (editing) await updateDoc(doc(db, 'apparel', editing), data);
      else await addDoc(collection(db, 'apparel'), data);
      setDialogOpen(false);
      setMsg(editing ? 'Item updated.' : 'Item added.');
      setTimeout(() => setMsg(''), 3000);
      load();
    } catch (e) { setError('Save failed: ' + e.message); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? Past orders are unaffected.`)) return;
    try { await deleteDoc(doc(db, 'apparel', item.id)); load(); }
    catch (e) { setError('Delete failed: ' + e.message); }
  };

  const fieldSx = { mb: 2 };

  return (
    <Layout>
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1000, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Torch Store Items</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/admin')}>
            Back to Dashboard
          </Button>
          <Button variant="outlined" endIcon={<OpenInNewIcon />} onClick={() => window.open('/torch-store', '_blank')}>
            Preview Store
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>Add Item</Button>
        </Box>
      </Box>

      {bulkOrders.map(order => {
        const rows = signups[order.id] || [];
        const committed = rows.reduce((t, r) => t + (Number(r.quantity) || 0), 0);
        const each = (Number(order.unitPriceCents) || 0) / 100;
        const income = committed * each;
        // By the ball: a row can be part paid (one of two).
        const collected = rows.reduce((t, r) => t + paidQty(r), 0) * each;
        const owing = rows.reduce((t, r) => t + owedQty(r), 0) * each;
        const deliveredQty = rows.filter(r => r.delivered === true).reduce((t, r) => t + (Number(r.quantity) || 0), 0);
        // Rows ticked before methods were recorded have none; they show as
        // "not recorded" rather than being folded into one of the three.
        const byMethod = {};
        rows.filter(r => paidQty(r) > 0).forEach(r => {
          const m = r.paidMethod || 'Not recorded';
          byMethod[m] = (byMethod[m] || 0) + paidQty(r) * each;
        });
        const cost = orderCost(costs[order.id], costEstimate, committed);
        // Collected less what it cost: the spares and the shipping are where an
        // order at a few dollars over cost goes negative.
        const net = cost ? Math.round(collected * 100) - cost.total : 0;
        const methodOrder = [...PAID_METHODS, 'Not recorded'].filter(m => byMethod[m])
          .concat(Object.keys(byMethod).filter(m => ![...PAID_METHODS, 'Not recorded'].includes(m)));
        return (
          <Paper key={order.id} variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{order.name}</Typography>
              {/* Collecting and on hold both take names; ordered and arrived
                  close the list. Changed here so waiting on a manufacturer does
                  not need a deploy. */}
              <TextField select size="small" value={order.status || 'collecting'}
                disabled={bulkBusy} onChange={e => setStatus(order, e.target.value)}
                sx={{ minWidth: 150 }}>
                <MenuItem value="collecting">Collecting</MenuItem>
                <MenuItem value="on_hold">On hold — no stock</MenuItem>
                <MenuItem value="ordered">Ordered</MenuItem>
                <MenuItem value="arrived">Arrived</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </TextField>
              <Typography variant="body2" sx={{ color: '#555' }}>
                {committed} committed
                {' · '}${income.toFixed(2)} at ${each.toFixed(2)} each
                {' · '}{rows.length} on the list
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button size="small" variant="contained" startIcon={<AddIcon />} disabled={bulkBusy}
                onClick={() => { setAddRow(emptyAddRow); setAddFor(order); }}>Add person</Button>
              <Button size="small" variant="outlined" disabled={bulkBusy}
                onClick={() => openCost(order)}>Cost</Button>
              <Button size="small" variant="outlined" disabled={committed === 0}
                onClick={() => exportSignups(order)}>Contacts CSV</Button>
            </Box>
            {/* Nobody has paid: this list is pledges, and the money is asked for
                once the order is placed. */}
            {/* Collected against committed, because the gap is the thing to act
                on: who still owes before the order goes in. */}
            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
              <span style={{ color: '#1E6B3C', fontWeight: 700 }}>${collected.toFixed(2)} collected</span>
              <span style={{ color: '#888' }}> of ${income.toFixed(2)}</span>
              {methodOrder.length > 0 && (
                <span style={{ color: '#555' }}>
                  {' ('}{methodOrder.map(m => `${m} $${byMethod[m].toFixed(2)}`).join(' · ')}{')'}
                </span>
              )}
              {owing > 0 && <span style={{ color: '#B26A00', fontWeight: 700 }}> · ${owing.toFixed(2)} still owing</span>}
              {deliveredQty > 0 && <span style={{ color: '#555' }}> · {deliveredQty} of {committed} handed out</span>}
              <span style={{ color: '#888' }}>
                {' — nothing is charged by the app. Tick Paid when the money reaches you.'}
              </span>
            </Typography>
            {cost && (
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#555' }}>
                <strong>{cost.placed ? `Ordered ${cost.quantity} for ${money(cost.total)}` : `Estimated cost for ${cost.quantity}: ${money(cost.total)}`}</strong>
                {' ('}{money(cost.items)} items · {money(cost.shipping)} shipping · {money(cost.tax)} tax{cost.taxEstimated ? ' est.' : ''}{')'}
                {cost.quantity > 0 && ` · ${money(Math.round(cost.total / cost.quantity))} each`}
                {cost.placed && cost.quantity > committed && ` · ${cost.quantity - committed} spare`}
                {' · '}
                <span style={{ color: net < 0 ? '#C62828' : '#1E6B3C', fontWeight: 700 }}>
                  {net < 0 ? `${money(-net)} out of pocket` : `${money(net)} ahead`}
                </span>
                {cost.placed && cost.quantity > committed && (
                  <span>, {money(net + (cost.quantity - committed) * Math.round(each * 100))} once the spares sell</span>
                )}
                {!cost.placed && costEstimate?.basedOn && (
                  <span style={{ color: '#888' }}>{' — shipping and tax as on '}{costEstimate.basedOn}</span>
                )}
              </Typography>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Phone</strong></TableCell>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell align="right"><strong>Qty</strong></TableCell>
                    <TableCell align="right"><strong>Owes</strong></TableCell>
                    <TableCell align="center"><strong>Paid</strong></TableCell>
                    <TableCell align="center"><strong>Picked up</strong></TableCell>
                    <TableCell align="right"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={8} sx={{ color: '#888' }}>Nobody on the list yet.</TableCell></TableRow>
                  )}
                  {rows.map(r => (
                    <TableRow key={r.id} sx={{
                      opacity: (Number(r.quantity) || 0) > 0 ? 1 : 0.55,
                      // Paid rows go green so the list reads as what is left to
                      // chase rather than a list to re-read. Picked up is a
                      // shade deeper: the row is finished with.
                      // Anything still owing is amber, part paid or not paid at
                      // all: the yellow rows are the ones to chase. A row at zero
                      // owes nothing and stays plain.
                      backgroundColor: r.delivered === true ? '#D7F0DC'
                        : r.paid === true ? '#EAF7EC' : owedQty(r) > 0 ? '#FFF4DE' : 'inherit',
                      '&:hover': {
                        backgroundColor: r.delivered === true ? '#CBEAD2'
                          : r.paid === true ? '#DFF2E3' : owedQty(r) > 0 ? '#FCEBC6' : 'rgba(0,0,0,0.03)',
                      },
                    }}>
                      <TableCell>
                        {r.firstName} {r.lastName}
                        {r.isMember && <Chip size="small" label="member" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />}
                        {!r.isMember && (() => {
                          const matches = usersByPhone[phoneKey(r.phone)] || [];
                          if (matches.length === 0) return null;
                          const names = matches.map(u => `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'an account')
                            .join(', ');
                          return (
                            <Tooltip title={`Phone matches ${names}`}>
                              <Chip size="small" label="member" variant="outlined"
                                sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell>{r.phone || '—'}</TableCell>
                      <TableCell>{r.email || '—'}</TableCell>
                      <TableCell align="right">
                        <TextField size="small" type="number" value={Number(r.quantity) || 0}
                          disabled={bulkBusy}
                          onChange={e => setQuantity(r, Math.max(0, Number(e.target.value) || 0))}
                          inputProps={{ min: 0, max: 20, style: { width: 56, textAlign: 'right' } }} />
                      </TableCell>
                      <TableCell align="right">
                        {/* What is still to collect, so a part-paid row shows its
                            balance rather than the whole price. */}
                        {(Number(r.quantity) || 0) > 0
                          ? `$${(owedQty(r) * each).toFixed(2)}`
                          : '—'}
                        {paidQty(r) > 0 && paidQty(r) < (Number(r.quantity) || 0) && (
                          <Typography variant="caption" sx={{ display: 'block', color: '#B26A00' }}>
                            paid {paidQty(r)} of {Number(r.quantity) || 0}
                          </Typography>
                        )}
                        {paidQty(r) > (Number(r.quantity) || 0) && (
                          <Typography variant="caption" sx={{ display: 'block', color: '#1565C0' }}>
                            paid for {paidQty(r)} · ${((paidQty(r) - (Number(r.quantity) || 0)) * each).toFixed(2)} credit
                          </Typography>
                        )}
                        {/* Money given back. Refunds happen outside the app, the
                            same as payments, so this is the only record of one. */}
                        {r.refundNote && (
                          <Typography variant="caption" sx={{ display: 'block', color: '#6A1B9A', maxWidth: 200, ml: 'auto' }}>
                            {r.refundNote}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {/* Only for somebody actually ordering: a row at zero has
                            nothing to pay, so a tick box there would be noise. */}
                        {(Number(r.quantity) || 0) > 0 ? (
                          <Tooltip title={r.paid && r.paidAt?.toDate
                            ? `Paid ${r.paidAt.toDate().toLocaleDateString()}`
                            : 'Pick how they paid when the money reaches you'}>
                            {/* One choice for both questions: Unpaid, or paid and how.
                                Rows ticked before methods were recorded keep a
                                "Paid (not recorded)" choice until one is picked. */}
                            <TextField select size="small" disabled={bulkBusy}
                              value={r.paid === true ? (r.paidMethod || 'paid') : paidQty(r) > 0 ? 'partial' : 'unpaid'}
                              onChange={e => (e.target.value === 'unpaid'
                                ? setPaid(r, false)
                                : setPaid(r, true, e.target.value))}
                              sx={{ minWidth: 110, '& .MuiSelect-select': { py: 0.5, fontSize: '0.85rem' } }}>
                              <MenuItem value="unpaid">Unpaid</MenuItem>
                              {PAID_METHODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                              {/* Picking a method from here marks the rest paid too. */}
                              {r.paid !== true && paidQty(r) > 0 && (
                                <MenuItem value="partial" disabled>Part paid</MenuItem>
                              )}
                              {r.paid === true && !r.paidMethod && (
                                <MenuItem value="paid" disabled>Paid (not recorded)</MenuItem>
                              )}
                              {r.paid === true && r.paidMethod && !PAID_METHODS.includes(r.paidMethod) && (
                                <MenuItem value={r.paidMethod} disabled>{r.paidMethod}</MenuItem>
                              )}
                            </TextField>
                          </Tooltip>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="center">
                        {/* Its own step. The club does hand somebody theirs
                            before the money arrives, so this does not imply
                            Paid — and on the store page it is the second tick. */}
                        {(Number(r.quantity) || 0) > 0 ? (
                          <Tooltip title={r.delivered
                            ? `Picked up${r.deliveredAt?.toDate ? ' ' + r.deliveredAt.toDate().toLocaleDateString() : ''} — click to undo`
                            : 'Tick when they have it in their hands'}>
                            <Switch size="small" checked={r.delivered === true} disabled={bulkBusy}
                              onChange={e => setDelivered(r, e.target.checked)} />
                          </Tooltip>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Edit name, phone, email, quantity or what is paid">
                          <IconButton size="small" disabled={bulkBusy} onClick={() => openEditRow(r)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove from this order">
                          <IconButton size="small" color="error" disabled={bulkBusy} onClick={() => removeRow(r)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        );
      })}

      {msg && <Alert severity="success" sx={{ mb: 2 }}>{msg}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f1f3f5' }}>
              <TableCell>Item</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell>Sizes</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Order</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ color: '#888', py: 4 }}>
                No items yet — click Add Item to create your first product.
              </TableCell></TableRow>
            )}
            {items.map(item => (
              <TableRow key={item.id} hover>
                <TableCell>
                  <Typography sx={{ fontWeight: 600 }}>{item.name}</Typography>
                  {item.description && <Typography variant="caption" sx={{ color: '#888' }}>{item.description}</Typography>}
                </TableCell>
                <TableCell align="right">${Number(item.price || 0).toFixed(2)}</TableCell>
                <TableCell>{Array.isArray(item.sizes) && item.sizes.length ? item.sizes.join(' · ') : '—'}</TableCell>
                <TableCell align="center">
                  <Chip size="small" label={item.active !== false ? 'Active' : 'Hidden'}
                    color={item.active !== false ? 'success' : 'default'} variant="outlined" />
                </TableCell>
                <TableCell align="center">{item.sortOrder ?? '—'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(item)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(item)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(costFor)} onClose={() => setCostFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{costFor?.name} — cost</DialogTitle>
        <DialogContent>
          <Typography variant="caption" sx={{ display: 'block', color: '#888', mb: 2 }}>
            Admins only. The store page never shows what the club pays.
          </Typography>
          <TextField fullWidth size="small" label="Cost per item ($)" value={costForm.unitCost} sx={fieldSx}
            onChange={e => setCostForm(f => ({ ...f, unitCost: e.target.value }))}
            helperText="Before shipping and tax. Used to estimate an order not placed yet." />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>The order as placed</Typography>
          <TextField fullWidth size="small" type="number" label="Quantity ordered" value={costForm.quantity} sx={fieldSx}
            onChange={e => setCostForm(f => ({ ...f, quantity: e.target.value }))} />
          <TextField fullWidth size="small" label="Items subtotal ($)" value={costForm.subtotal} sx={fieldSx}
            onChange={e => setCostForm(f => ({ ...f, subtotal: e.target.value }))} />
          <TextField fullWidth size="small" label="Shipping ($)" value={costForm.shipping} sx={fieldSx}
            onChange={e => setCostForm(f => ({ ...f, shipping: e.target.value }))} />
          <TextField fullWidth size="small" label="Tax ($)" value={costForm.tax} sx={{ mb: 1 }}
            onChange={e => setCostForm(f => ({ ...f, tax: e.target.value }))} />
          <FormControlLabel label="Tax is an estimate (final amount not confirmed yet)"
            control={<Switch size="small" checked={costForm.taxEstimated}
              onChange={e => setCostForm(f => ({ ...f, taxEstimated: e.target.checked }))} />} />
          <FormControlLabel label="Use this shipping and tax rate to estimate the next order"
            control={<Switch size="small" checked={costForm.saveEstimate}
              onChange={e => setCostForm(f => ({ ...f, saveEstimate: e.target.checked }))} />} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCostFor(null)}>Cancel</Button>
          <Button variant="contained" disabled={bulkBusy} onClick={saveCost}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(addFor)} onClose={() => setAddFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Add to {addFor?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField label="First name" required autoFocus fullWidth value={addRow.firstName}
                onChange={e => setAddRow(f => ({ ...f, firstName: e.target.value }))} />
              <TextField label="Last name" fullWidth value={addRow.lastName}
                onChange={e => setAddRow(f => ({ ...f, lastName: e.target.value }))} />
            </Box>
            <TextField label="Phone" value={addRow.phone}
              onChange={e => setAddRow(f => ({ ...f, phone: e.target.value }))} />
            <TextField label="Email" type="email" value={addRow.email}
              onChange={e => setAddRow(f => ({ ...f, email: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField label="Quantity" type="number" fullWidth value={addRow.quantity}
                onChange={e => setAddRow(f => ({ ...f, quantity: e.target.value }))}
                inputProps={{ min: 0, max: 20 }} />
              <TextField select label="Payment" fullWidth value={addRow.payment}
                onChange={e => setAddRow(f => ({ ...f, payment: e.target.value }))}>
                <MenuItem value="unpaid">Unpaid</MenuItem>
                {PAID_METHODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFor(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveAddRow} disabled={bulkBusy}>Add</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editRow)} onClose={() => setEditRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit {editRow?.firstName} {editRow?.lastName}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
            {editRow?.isMember && (
              <Alert severity="info">
                A member. Their name and phone come from their account and are copied back
                here the next time they change their own order.
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField label="First name" required fullWidth value={addRow.firstName}
                onChange={e => setAddRow(f => ({ ...f, firstName: e.target.value }))} />
              <TextField label="Last name" fullWidth value={addRow.lastName}
                onChange={e => setAddRow(f => ({ ...f, lastName: e.target.value }))} />
            </Box>
            <TextField label="Phone" value={addRow.phone}
              onChange={e => setAddRow(f => ({ ...f, phone: e.target.value }))} />
            <TextField label="Email" type="email" value={addRow.email}
              onChange={e => setAddRow(f => ({ ...f, email: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField label="Quantity" type="number" fullWidth value={addRow.quantity}
                onChange={e => setAddRow(f => ({ ...f, quantity: e.target.value }))}
                inputProps={{ min: 0, max: 20 }} />
              <TextField label="Paid for" type="number" fullWidth value={addRow.paidFor}
                onChange={e => setAddRow(f => ({ ...f, paidFor: e.target.value }))}
                inputProps={{ min: 0, max: 20 }}
                helperText={(() => {
                  const owed = (Number(addRow.quantity) || 0) - (Number(addRow.paidFor) || 0);
                  const each = (Number(bulkOrders.find(o => o.id === editRow?.orderId)?.unitPriceCents) || 0) / 100;
                  return owed > 0 ? `$${(owed * each).toFixed(2)} still owing`
                    : owed < 0 ? `$${(-owed * each).toFixed(2)} credit` : 'Paid in full';
                })()} />
            </Box>
            <TextField select label="Paid by" value={addRow.payment}
              disabled={!(Number(addRow.paidFor) > 0)}
              onChange={e => setAddRow(f => ({ ...f, payment: e.target.value }))}>
              <MenuItem value="unpaid" disabled>Choose</MenuItem>
              {PAID_METHODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              {addRow.payment && addRow.payment !== 'unpaid' && !PAID_METHODS.includes(addRow.payment) && (
                <MenuItem value={addRow.payment}>{addRow.payment}</MenuItem>
              )}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveEditRow} disabled={bulkBusy}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Item' : 'New Item'}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <TextField fullWidth label="Name *" value={form.name} sx={fieldSx}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <TextField fullWidth label="Description" value={form.description} sx={fieldSx} multiline rows={2}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          <TextField fullWidth label="Price (USD) *" value={form.price} sx={fieldSx} type="number"
            onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
          <TextField fullWidth label="Sizes (comma-separated, blank = no sizes)" value={form.sizesText} sx={fieldSx}
            helperText="e.g. S, M, L, XL — leave empty for one-size items like backpacks"
            onChange={e => setForm(f => ({ ...f, sizesText: e.target.value }))} />
          <TextField fullWidth label="Image URL" value={form.imageUrl} sx={fieldSx}
            helperText="Firebase Storage URL, or /filename.png for an image in public/"
            onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))} />
          <TextField fullWidth label="Sort order" value={form.sortOrder} sx={fieldSx} type="number"
            helperText="Lower numbers appear first"
            onChange={e => setForm(f => ({ ...f, sortOrder: e.target.value }))} />
          <FormControlLabel control={
            <Switch checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />
          } label="Active (visible in the store)" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>{editing ? 'Save' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
    </Layout>
  );
}
